import { Box, Typography } from "@mui/material";

import ChunkContent from "../sources/ChunkContent";
import { useUIStore } from "../../stores/UIStore";
import { TArtifact } from "../../types";
import ArtifactDisplay from "./ArtifactDisplay";
import ToolTrackerView from "./TaskStatusTabs";
import { getToolResultRenderer } from "./toolResultRenderers";

const RawText = ({ value }: { value: unknown }) => {
  const text =
    typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return (
    <Box sx={{ p: 2 }}>
      <Typography
        component="pre"
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "monospace",
          fontSize: "0.8125rem",
          lineHeight: 1.5,
          m: 0,
          color: "text.primary",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

type TArtifactItemList = {
  artifacts: TArtifact[];
};

const ArtifactItemList = ({ artifacts }: TArtifactItemList) => {
  const { rightSidebar } = useUIStore();
  const currentArtifactId = rightSidebar.currentArtifactId;

  const currentArtifact = artifacts.find((a) => a.id === currentArtifactId);

  if (!currentArtifact) {
    return (
      <Box data-testid="artifact-item-list-empty" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No artifact selected
        </Typography>
      </Box>
    );
  }

  // Key forces React to remount component when artifact changes
  return (
    <Box
      data-testid={`artifact-item-display-${currentArtifactId}`}
      data-artifact-id={currentArtifactId}
    >
      <ArtifactContent key={currentArtifactId} artifact={currentArtifact} />
    </Box>
  );
};

// Component for rendering artifact content (remounted by parent key prop)
const ArtifactContent = ({ artifact }: { artifact: TArtifact }) => {
  const result = artifact;

  if (!result?.content?.text) {
    return (
      <Box data-testid="artifact-content-empty" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No content available
        </Typography>
      </Box>
    );
  }

  // Handle background task type
  if (
    result?.content?.text?.type === "background_task" ||
    (result?.content?.text?.result &&
      typeof result?.content.text?.result === "object" &&
      result?.content?.text?.result?.type === "background_task")
  ) {
    const taskData =
      result?.content?.text?.type === "background_task"
        ? result.content.text
        : result.content.text.result;
    return <ToolTrackerView key={result.id} taskData={taskData} />;
  }

  // Handle tool_result message type with background task
  if (
    result?.content?.text?.message_type === "tool_result" &&
    result?.content?.text?.result &&
    typeof result?.content?.text?.result === "object" &&
    result?.content?.text?.result?.type === "background_task"
  ) {
    const taskData = result.content.text.result;
    return <ToolTrackerView key={result.id} taskData={taskData} />;
  }

  // Handle object content
  if (typeof result?.content?.text === "object") {
    // Check if it's an artifact result object
    if (
      result?.content?.text !== null &&
      typeof result?.content?.text === "object" &&
      "artifact_id" in result?.content?.text &&
      "message" in result?.content?.text
    ) {
      // This is an artifact result, use ArtifactDisplay
      const artifactResult = result?.content?.text as any;

      return (
        <div key={result.id}>
          <ArtifactDisplay
            artifactId={artifactResult.artifact_id}
            artifactName={artifactResult.artifact_name}
            message={artifactResult.message}
          />
        </div>
      );
    }

    // Check for custom renderers (e.g., search results)
    const contentData = result?.content?.text as Record<string, unknown>;
    let toolResultData: unknown =
      contentData?.message_type === "tool_result"
        ? contentData?.result
        : contentData;

    // Parse result if it's a JSON string (backend sometimes returns stringified arrays)
    if (typeof toolResultData === "string") {
      try {
        toolResultData = JSON.parse(toolResultData);
      } catch {
        // Not valid JSON, keep as string
      }
    }

    const toolType = (result as any)?.toolName || contentData?.tool_type;

    const Renderer = getToolResultRenderer(toolResultData, toolType as string);
    if (Renderer) {
      return (
        <div
          data-testid={`artifact-content-custom-${result.id}`}
          key={result.id}
        >
          <Renderer data={toolResultData} />
        </div>
      );
    }

    // Handle simple string results (e.g., "No Results", error messages)
    if (typeof toolResultData === "string") {
      return (
        <Box
          data-testid={`artifact-content-string-${result.id}`}
          key={result.id}
        >
          <RawText value={toolResultData} />
        </Box>
      );
    }

    // Check if it's a valid object that can be displayed
    try {
      // Object-shaped tool result — route through ChunkContent for interactive
      // KeyValueGrid + auto-table rendering (arrays of records become real
      // tables). RawText is reserved for the genuinely-string paths below.
      if (
        result?.content?.text !== null &&
        typeof result?.content?.text === "object"
      ) {
        return (
          <Box
            data-testid={`artifact-content-json-${result.id}`}
            key={result.id}
            sx={{ p: 2 }}
          >
            <ChunkContent data={toolResultData ?? result.content.text} />
          </Box>
        );
      }
    } catch (error) {
      console.error("Error rendering artifact object:", error);
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="error">
            Error displaying artifact content
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {String(result?.content?.text)}
          </Typography>
        </Box>
      );
    }
  }

  // Handle string content
  if (typeof result?.content?.text === "string") {
    // Try to parse as JSON if it looks like JSON
    if (
      result?.content?.text.trim().startsWith("{") ||
      result?.content?.text.trim().startsWith("[")
    ) {
      try {
        const parsedContent = JSON.parse(result?.content?.text);

        // Check if the parsed content is an artifact result
        if (
          typeof parsedContent === "object" &&
          parsedContent !== null &&
          "artifact_id" in parsedContent &&
          "message" in parsedContent
        ) {
          // This is an artifact result, use ArtifactDisplay
          return (
            <div key={result.id}>
              <ArtifactDisplay
                artifactId={parsedContent.artifact_id}
                artifactName={parsedContent.artifact_name}
                message={parsedContent.message}
              />
            </div>
          );
        }

        // Parsed-JSON tool result — route through ChunkContent so arrays of
        // records render as tables and objects render as KeyValueGrid.
        return (
          <Box
            data-testid={`artifact-content-parsed-json-${result.id}`}
            key={result.id}
            sx={{ p: 2 }}
          >
            <ChunkContent data={parsedContent} />
          </Box>
        );
      } catch (error) {
        // If parsing fails, render as raw text
        return (
          <Box key={result.id}>
            <RawText value={result.content.text} />
          </Box>
        );
      }
    } else {
      // Plain text artifact — raw
      return (
        <Box key={result.id}>
          <RawText value={result.content.text} />
        </Box>
      );
    }
  }

  // Fallback for any other type
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Unsupported content type: {typeof result?.content?.text}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {String(result?.content?.text)}
      </Typography>
    </Box>
  );
};

export default ArtifactItemList;
